import { FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, ExternalLink, Plus, QrCode, ScanSearch, Upload } from "lucide-react";
import { api, formatApiError, type Hotel, type Location, type QrRoute, type SubLocation } from "../api/client";

export function QrRoutesPage() {
  const queryClient = useQueryClient();
  const [selectedHotelId, setSelectedHotelId] = useState("");
  const [bulkCsv, setBulkCsv] = useState("");
  const [lastCreatedRoute, setLastCreatedRoute] = useState<QrRoute | null>(null);
  const routes = useQuery({ queryKey: ["qr-routes"], queryFn: () => api<QrRoute[]>("/api/admin/qr-routes") });
  const hotels = useQuery({ queryKey: ["hotels"], queryFn: () => api<Hotel[]>("/api/admin/hotels") });
  const locations = useQuery({ queryKey: ["locations"], queryFn: () => api<Location[]>("/api/admin/locations") });
  const subLocations = useQuery({ queryKey: ["sub-locations"], queryFn: () => api<SubLocation[]>("/api/admin/sub-locations") });
  const firstRoute = routes.data?.[0];
  const activeHotel = hotels.data?.find((hotel) => hotel.id === selectedHotelId) ?? hotels.data?.[0];
  const scopedLocations = useMemo(() => {
    const hotelId = selectedHotelId || activeHotel?.id;
    return (locations.data ?? []).filter((location) => !hotelId || location.hotelId === hotelId);
  }, [activeHotel?.id, locations.data, selectedHotelId]);
  const scopedSubLocations = useMemo(() => {
    const locationIds = new Set(scopedLocations.map((location) => location.id));
    return (subLocations.data ?? []).filter((subLocation) => locationIds.has(subLocation.locationId));
  }, [scopedLocations, subLocations.data]);

  const createQr = useMutation({
    mutationFn: (body: unknown) => api<QrRoute>("/api/admin/qr-routes", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: (route) => {
      setLastCreatedRoute(route);
      queryClient.invalidateQueries({ queryKey: ["qr-routes"] });
    }
  });

  const bulkImport = useMutation({
    mutationFn: (body: unknown) => api<{ count: number }>("/api/admin/qr-routes/bulk-import", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: () => {
      setBulkCsv("");
      queryClient.invalidateQueries({ queryKey: ["qr-routes"] });
    }
  });
  const updateQr = useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) => api<QrRoute>(`/api/admin/qr-routes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["qr-routes"] })
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const hotel = hotels.data?.find((item) => item.id === String(form.get("hotelId")));
    const location = locations.data?.find((item) => item.id === String(form.get("locationId")));
    const subLocation = subLocations.data?.find((item) => item.id === String(form.get("subLocationId")));
    if (!hotel || !location) return;
    createQr.mutate({
      hotelId: hotel.id,
      locationId: location.id,
      subLocationId: subLocation?.id,
      hotelCode: hotel.code,
      locationCode: location.code,
      unitCode: subLocation?.code ?? (String(form.get("unitCode") || "") || undefined),
      label: String(form.get("label") || `${hotel.name} - ${location.name}${subLocation ? ` - ${subLocation.name}` : ""}`),
      status: "active",
      campaignTags: {
        floor: location.floor,
        zone: location.zone,
        subLocationType: subLocation?.type
      }
    });
    event.currentTarget.reset();
  }

  function submitBulk(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const hotel = hotels.data?.find((item) => item.id === String(form.get("hotelId")));
    const location = locations.data?.find((item) => item.id === String(form.get("locationId")));
    if (!hotel || !location) return;
    const rows = parseUnitCsv(bulkCsv).map((row) => ({
      hotelId: hotel.id,
      locationId: location.id,
      hotelCode: hotel.code,
      locationCode: location.code,
      unitCode: row.unitCode,
      label: row.label || `${hotel.name} - ${location.name} ${row.unitCode}`,
      status: "active",
      campaignTags: { floor: location.floor, zone: location.zone }
    }));
    if (rows.length) bulkImport.mutate({ rows });
  }

  function openQrPopup(path: string) {
    const popup = window.open(path, "qr-format-preview", "popup,width=980,height=780");
    if (popup) {
      popup.opener = null;
      popup.focus();
      return;
    }
    if (!popup) {
      window.open(path, "_blank", "noopener,noreferrer");
    }
  }

  function routeFormatButtons(route: QrRoute) {
    return (
      <>
        <button type="button" onClick={() => openQrPopup(publicQrUrl(route.shortPath, true))}><ScanSearch size={16} /> Provjeri</button>
        <button type="button" onClick={() => openQrPopup(publicQrUrl(route.shortPath))}><ExternalLink size={16} /> Otvori</button>
        <button type="button" onClick={() => openQrPopup(`/api/admin/qr-routes/${route.id}/download.svg`)}><Download size={16} /> SVG</button>
        <button type="button" onClick={() => openQrPopup(`/api/admin/qr-routes/${route.id}/download.png`)}><Download size={16} /> PNG</button>
        <button type="button" onClick={() => openQrPopup(`/api/admin/qr-routes/export-sheet.pdf?ids=${route.id}`)}><Download size={16} /> PDF</button>
      </>
    );
  }

  return (
    <div className="page-stack">
      <section className="tool-hero card qr-hero">
        <div>
          <span className="panel-eyebrow left">QR Campaign Routes</span>
          <h2>Stabilni short URL za svaki objekat, lokaciju i unit.</h2>
          <p>Stampani QR se ne mijenja; administracija mijenja mapiranje, status i campaign metadata.</p>
        </div>
        <div className="qr-preview-card">
          <QrCode size={72} />
          <strong>{firstRoute?.shortPath ?? "/a/r/14"}</strong>
          <span>{firstRoute ? publicQrUrl(firstRoute.shortPath) : `${publicQrOrigin()}/br/sp/r1`}</span>
        </div>
      </section>

      <section className="panel card">
        <div className="panel-header">
          <div>
            <h2>Novi QR</h2>
            <p>Kreiraj short URL za izabrani objekat, lokaciju i opcioni unit.</p>
          </div>
          <div className="toolbar">
            <button type="button" onClick={() => openQrPopup("/api/admin/qr-routes/export-sheet.pdf")}><Download size={16} /> PDF sheet</button>
          </div>
        </div>
        <form className="inline-form qr-create-form" onSubmit={submit}>
          <select name="hotelId" required value={selectedHotelId || activeHotel?.id || ""} onChange={(event) => setSelectedHotelId(event.target.value)}>
            {(hotels.data ?? []).map((hotel) => <option key={hotel.id} value={hotel.id}>{hotel.name}</option>)}
          </select>
          <select name="locationId" required disabled={!scopedLocations.length}>
            {!scopedLocations.length && <option value="">Nema lokacija za objekat</option>}
            {scopedLocations.map((location) => <option key={location.id} value={location.id}>{location.name} ({location.locationType.name})</option>)}
          </select>
          <select name="subLocationId">
            <option value="">Bez sublokacije / rucni kod</option>
            {scopedSubLocations.map((subLocation) => (
              <option key={subLocation.id} value={subLocation.id}>{subLocation.location.name} - {subLocation.name} ({subLocation.code})</option>
            ))}
          </select>
          <input name="unitCode" placeholder="Rucni kod ako nema sublokacije" pattern="[a-zA-Z0-9-]{1,8}" />
          <input name="label" placeholder="Label za admin i export" />
          <button type="submit" disabled={!scopedLocations.length}><Plus size={16} /> Kreiraj QR</button>
        </form>
        <div className="form-note">Finalni format za stampu: <code>{publicQrOrigin()}/hotel/location/sublocation</code>. Primjeri: <code>{publicQrOrigin()}/br/sp/r1</code>, <code>{publicQrOrigin()}/hg/lb/01</code>.</div>
        <div className="form-note">
          Preporuka za stampu: visok kontrast, ocuvan quiet zone, minimum 30 mm sirine, mat materijal i test skeniranje prije serijske stampe.
        </div>
        {lastCreatedRoute && (
          <div className="preview-result qr-created-result">
            <div>
              <strong>QR kreiran: {lastCreatedRoute.label}</strong>
              <span><code>{lastCreatedRoute.shortPath}</code> {publicQrUrl(lastCreatedRoute.shortPath)}</span>
            </div>
            <div className="row-actions">
              {routeFormatButtons(lastCreatedRoute)}
            </div>
          </div>
        )}
        {(createQr.error || updateQr.error) && <p className="form-error">{formatApiError(createQr.error ?? updateQr.error)}</p>}
      </section>

      <section className="panel card">
        <div className="panel-header">
          <div>
            <h2>Bulk CSV import</h2>
            <p>Za izabrani objekat i lokaciju unesi CSV: unitCode,label. Primjer: 302,Soba 302.</p>
          </div>
          <Upload size={20} />
        </div>
        <form className="compact-form" onSubmit={submitBulk}>
          <select name="hotelId" required value={selectedHotelId || activeHotel?.id || ""} onChange={(event) => setSelectedHotelId(event.target.value)}>
            {(hotels.data ?? []).map((hotel) => <option key={hotel.id} value={hotel.id}>{hotel.name}</option>)}
          </select>
          <select name="locationId" required disabled={!scopedLocations.length}>
            {!scopedLocations.length && <option value="">Nema lokacija za objekat</option>}
            {scopedLocations.map((location) => <option key={location.id} value={location.id}>{location.name} ({location.locationType.name})</option>)}
          </select>
          <textarea rows={6} value={bulkCsv} onChange={(event) => setBulkCsv(event.target.value)} placeholder={"302,Soba 302\n303,Soba 303\n14,Restoran sto 14"} />
          <button type="submit" disabled={!scopedLocations.length}><Upload size={16} /> Importuj QR rute</button>
        </form>
        {bulkImport.error && <p className="form-error">{formatApiError(bulkImport.error)}</p>}
      </section>

      <section className="panel card">
        <div className="panel-header">
          <div>
            <h2>QR konfiguracije</h2>
            <p>Pregled aktivnih, pauziranih i arhiviranih QR ruta.</p>
          </div>
        </div>
        <div className="table-shell">
          <table>
            <thead>
              <tr>
                <th>Ruta</th>
                <th>Hotel</th>
                <th>Lokacija</th>
                <th>Status</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>
              {(routes.data ?? []).map((route) => (
                <tr key={route.id}>
                  <td><code>{route.shortPath}</code><span>{route.label}</span></td>
                  <td>{route.hotel.name}</td>
                  <td>{route.location.name}<span>{route.subLocation?.name ?? route.unitCode ?? ""}</span></td>
                  <td><span className="status">{route.status}</span></td>
                  <td className="row-actions">
                    {routeFormatButtons(route)}
                    <button disabled={route.status === "active"} onClick={() => updateQr.mutate({ id: route.id, body: { status: "active" } })}>Active</button>
                    <button disabled={route.status === "paused"} onClick={() => updateQr.mutate({ id: route.id, body: { status: "paused" } })}>Pause</button>
                    <button disabled={route.status === "archived"} onClick={() => updateQr.mutate({ id: route.id, body: { status: "archived" } })}>Archive</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function parseUnitCsv(value: string): Array<{ unitCode: string; label?: string }> {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [unitCode, ...labelParts] = line.split(",");
      return {
        unitCode: (unitCode ?? "").trim(),
        label: labelParts.join(",").trim() || undefined
      };
    })
    .filter((row) => row.unitCode && row.unitCode.toLowerCase() !== "unitcode");
}

function publicQrUrl(shortPath: string, inspect = false) {
  const url = new URL(shortPath, publicQrOrigin());
  if (inspect) url.searchParams.set("inspect", "1");
  return url.toString();
}

function publicQrOrigin() {
  const configured = import.meta.env.VITE_PUBLIC_ORIGIN;
  if (typeof configured === "string" && configured.trim()) return configured.replace(/\/$/, "");
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return `${window.location.protocol}//${window.location.hostname}:3301`;
  }
  return window.location.origin;
}
